import { $PropertyType } from "utility-types";
import type { IntlNotification } from "../../utils/notifications";
import type { CustomerCart, CustomerCartItem, CustomerCartItems } from "../../model/CustomerCartTypes";
import type { CartPrices } from "../../model/Prices";
import { ServiceError } from "../BaseTypes";
import type { MovPerSupplierInfo } from "../../model/CommonCartTypes";
import { createDummyArticle } from "../../model/ArticleTypes";
import { floorToThreeDecimalPlaces, minQuantity } from "../../utils/NumberUtil";
import omit from "../../utils/omit";
import { intlRoot } from "../../IntlRootMixin";

export type EventStatusType = "Pending" | "Confirmed" | "Failure" | "Success";
export type EventStatus = {
	type: EventStatusType;
	error?: ServiceError;
};
export class CustomerCartEvent {
	_data: {
		eventId: string;
		cartId: string;
		customerId: string;
		seqNum: number;
		status: EventStatus;
		prices: CartPrices | null | undefined;
		timestamp: number | null | undefined;
		movPerSupplierInfo?: MovPerSupplierInfo;
	};
	_payload: CartEventPayload;
	_resetTimeoutId: any;

	constructor(data: $PropertyType<CustomerCartEvent, "_data">, payload: CartEventPayload) {
		this._data = data;
		this._payload = payload;
	}

	applyTo(cart: CustomerCart): CustomerCart | null {
		this._verifyApplicableTo(cart);

		const updatedState = this._applyPayload(cart);

		return updatedState
			? {
					...this._withPrices(updatedState),
					movPerSupplierInfo: this.movPerSupplierInfo ?? cart.movPerSupplierInfo,
					cartVersion: this.seqNum,
					eventHistory: [...cart.eventHistory, this.eventId],
				}
			: null;
	}

	_applyPayload(cart: CustomerCart): CustomerCart | null | undefined {
		if (this.hasError()) {
			return cart;
		}

		const updatedState = this.payload.applyTo(cart, this.status);

		if (
			!updatedState ||
			this._payload instanceof CheckedOutPayload ||
			this._payload instanceof CartSubmittedPayload
		) {
			return updatedState;
		}

		return { ...updatedState, orderData: { ...updatedState.orderData, checkoutId: null } };
	}

	_withPrices(cart: CustomerCart): CustomerCart {
		const prices = this._data.prices;

		if (
			((this.status.type === "Pending" || this._payload instanceof ItemCommentSetPayload) && !prices) ||
			this.hasError()
		) {
			return cart;
		}

		const items = Object.fromEntries(
			Object.entries(cart.items).map(([key, item]) => [
				key,
				{
					...item,
					prices: prices?.articlePrices?.[item.bundleId],
				},
			]),
		);

		const totalPrices = prices?.total;
		return { ...cart, totalPrices, items };
	}

	_verifyApplicableTo(cart: CustomerCart) {
		if (cart.cartId !== this.cartId || cart.customerId !== this.customerId) {
			throw new ServiceError({
				code: "OC-806",
				message: "Tried to apply an event to a wrong cart",
				type: "ui-error",
			});
		}

		if (this.status.type !== "Pending" && cart.cartVersion + 1 !== this.seqNum) {
			throw new ServiceError({
				code: "OC-807",
				message: "Cart event sequence was not respected",
				type: "ui-error",
			});
		}
	}

	get eventId(): string {
		return this._data.eventId;
	}

	get cartId(): string {
		return this._data.cartId;
	}

	get customerId(): string {
		return this._data.customerId;
	}

	get seqNum(): number {
		return this._data.seqNum;
	}

	get payload(): CartEventPayload {
		return this._payload;
	}

	get age(): number {
		return new Date().getTime() - (this._data.timestamp || 0);
	}

	get status(): EventStatus {
		return this._data.status;
	}

	get errorValue(): string | null | undefined {
		return this.hasError() ? (this._data.status.error ? this._data.status.error.value : null) : null;
	}

	get errorCode(): string | null | undefined {
		return this.hasError() ? (this._data.status.error ? this._data.status.error.code : null) : null;
	}

	get resetTimeoutId(): any {
		return this._resetTimeoutId;
	}

	get movPerSupplierInfo(): MovPerSupplierInfo | undefined {
		return this._data.movPerSupplierInfo;
	}

	hasError() {
		return this._data.status.type === "Failure";
	}

	getParams(): string {
		return JSON.stringify(this.payload.data);
	}

	withStatus(status: EventStatus): CustomerCartEvent {
		return Object.assign(this, {
			_data: { ...this._data, status: status },
		});
	}

	withResetTimeoutId(id: any): CustomerCartEvent {
		return Object.assign(this, {
			_resetTimeoutId: id,
		});
	}
}
export interface CartEventPayload {
	readonly type: string;
	readonly data: Record<string, any>;
	applyTo(cart: CustomerCart, status: EventStatus): CustomerCart | null;
	getErrorMessage(id: string): IntlNotification;
}
export class CartCreatedPayload implements CartEventPayload {
	readonly type = "CartCreatedPayload";
	_data: {
		country: string;
		cardholderNumber: string;
		storeId: string;
		fsdAddressId: string | null | undefined;
	};

	constructor(data: $PropertyType<CartCreatedPayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo(cart: CustomerCart): CustomerCart {
		return {
			...cart,
			context: { ...this._data, isEmployeeCart: (cart.context && cart.context.isEmployeeCart) || false },
		};
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_CREATE_CART" }),
			id,
		};
	}
}
export class CartSavedPayload implements CartEventPayload {
	readonly type = "CartSavedPayload";
	_data: {
		cartName: string;
		savedCartId?: string;
		clearCart?: boolean;
	};

	constructor(data: $PropertyType<CartSavedPayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo(): null {
		return null;
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SAVE_CART" }),
			id,
		};
	}
}
export class CartDeletedPayload implements CartEventPayload {
	readonly type = "CartDeletedPayload";

	get data() {
		return {};
	}

	applyTo(): null {
		return null;
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_DELETE_CART" }),
			id,
		};
	}
}
export class ItemAddedPayload implements CartEventPayload {
	readonly type = "ItemAddedPayload";
	_data: {
		bundleId: string;
		quantity: number;
		comment: string | null | undefined;
		addedTimestamp: number;
		addedBy: string;
	};

	constructor(data: $PropertyType<ItemAddedPayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo(cart: CustomerCart, status: EventStatus): CustomerCart {
		const { bundleId, quantity, comment, addedTimestamp, addedBy } = this._data;
		return applyAddItem(cart, bundleId, quantity, status, comment, addedTimestamp, addedBy);
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_ADD_ITEM" }, { articleId: this._data.bundleId }),
			id,
		};
	}
}

function applyAddItem(
	cart: CustomerCart,
	bundleId: string,
	quantity: number,
	status: EventStatus,
	comment: string | null | undefined,
	addedTimestamp: number,
	addedBy: string,
): CustomerCart {
	if (quantity < minQuantity) {
		return cart;
	}

	const item: CustomerCartItem = {
		// @ts-expect-error
		bundleId,
		quantity: floorToThreeDecimalPlaces(quantity),
		comment,
		selectedServiceOptions: [],
		addedTimestamp,
		addedBy,
		orderable: true,
		unconfirmedState: status.type === "Pending",
		prices: null,
		manualPriceInfo: null,
		// @ts-expect-error
		outdated: true,
		...createDummyArticle(bundleId),
	};
	return { ...cart, items: { ...cart.items, [bundleId]: item } };
}

export class ItemBatchAddedPayload implements CartEventPayload {
	readonly type = "ItemBatchAddedPayload";
	_data: {
		addedItemData: Record<
			string,
			{
				bundleId: string;
				quantity: number;
				comment: string | null | undefined;
				addedTimestamp: number;
				addedBy: string;
			}
		>;
	};

	constructor(data: $PropertyType<ItemBatchAddedPayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo(cart: CustomerCart, status: EventStatus): CustomerCart {
		const { addedItemData } = this._data;
		return Object.values(addedItemData).reduce((agg, itemData) => {
			if (!(itemData instanceof Object)) return agg;
			const { bundleId, quantity, comment, addedTimestamp, addedBy } = itemData;
			return applyAddItem(agg, bundleId, quantity, status, comment, addedTimestamp, addedBy);
		}, cart);
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_ADD_ITEM_BATCH" }),
			id,
		};
	}
}
export class ItemQuantitySetPayload implements CartEventPayload {
	readonly type = "ItemQuantitySetPayload";
	_data: {
		bundleId: string;
		quantity: number;
		addedTimestamp: number;
		addedBy: string;
	};

	constructor(data: $PropertyType<ItemQuantitySetPayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo(cart: CustomerCart, status: EventStatus): CustomerCart {
		const { bundleId, quantity, addedTimestamp, addedBy } = this._data;
		const items = { ...cart.items };

		if (!items[bundleId]) {
			return applyAddItem(cart, bundleId, quantity, status, null, addedTimestamp, addedBy);
		}

		if (quantity < minQuantity) {
			delete items[bundleId];
			return { ...cart, items };
		}

		const item: CustomerCartItem = {
			...items[bundleId],
			quantity: floorToThreeDecimalPlaces(quantity),
			unconfirmedState: status.type === "Pending",
		};
		return { ...cart, items: { ...items, [bundleId]: item } };
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SET_ITEM_QUANTITY" }, { articleId: this._data.bundleId }),
			id,
		};
	}
}
export class ItemServiceOptionsSetPayload implements CartEventPayload {
	readonly type = "ItemServiceOptionsSetPayload";
	_data: {
		bundleId: string;
		serviceOptionIds: Array<string>;
	};

	constructor(data: $PropertyType<ItemServiceOptionsSetPayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo(cart: CustomerCart, status: EventStatus): CustomerCart {
		const { bundleId, serviceOptionIds } = this._data;
		return {
			...cart,
			items: {
				...cart.items,
				[bundleId]: {
					...cart.items[bundleId],
					selectedServiceOptions: serviceOptionIds,
					unconfirmedState: status.type === "Pending",
				},
			},
		};
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage(
				{ id: "ORC_ERROR_SET_ITEM_SERVICE_OPTIONS" },
				{ articleId: this._data.bundleId },
			),
			id,
		};
	}
}
export class ItemCommentSetPayload implements CartEventPayload {
	readonly type = "ItemCommentSetPayload";
	_data: {
		bundleId: string;
		comment: string;
	};

	constructor(data: $PropertyType<ItemCommentSetPayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo(cart: CustomerCart): CustomerCart {
		const { bundleId, comment } = this._data;
		const item: CustomerCartItem = { ...cart.items[bundleId], comment };
		return { ...cart, items: { ...cart.items, [bundleId]: item } };
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SET_ITEM_COMMENT" }),
			id,
		};
	}
}
export class ItemRemovedPayload implements CartEventPayload {
	readonly type = "ItemRemovedPayload";
	_data: {
		bundleId: string;
	};

	constructor(data: $PropertyType<ItemRemovedPayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo(cart: CustomerCart): CustomerCart {
		const { bundleId } = this._data;
		const items = { ...cart.items };
		delete items[bundleId];
		return { ...cart, items };
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_DELETE_ITEM" }, { articleId: this._data.bundleId }),
			id,
		};
	}
}
export class SetManualPricePayload implements CartEventPayload {
	readonly type = "ItemManualPriceSetPayload";
	_data: {
		bundleId: string;
		newPrice: number | null | undefined;
		reset: boolean;
		isBbd: boolean;
		approved: boolean;
	};

	constructor(data: $PropertyType<SetManualPricePayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo(cart: CustomerCart, status: EventStatus): CustomerCart {
		const { bundleId, newPrice, isBbd, reset, approved: isApproved } = this._data;
		const item: CustomerCartItem = {
			...cart.items[bundleId],
			unconfirmedState: status.type === "Pending",
			manualPriceInfo: !reset
				? {
						newPrice,
						isBbd,
						isApproved,
					}
				: null,
		};
		return { ...cart, items: { ...cart.items, [bundleId]: item } };
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SET_MANUAL_PRICE" }, { articleId: this._data.bundleId }),
			id,
		};
	}
}
export class DeliveryDateSetPayload implements CartEventPayload {
	readonly type = "DeliveryDateSetPayload";
	_data: {
		deliveryDate: string;
	};

	constructor(data: $PropertyType<DeliveryDateSetPayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo(cart: CustomerCart): CustomerCart {
		const result = { ...cart, orderData: { ...cart.orderData, deliveryDate: this._data.deliveryDate } };
		const shouldSetFsdDeliveryDate = cart.context?.fsdAddressId && cart.status === "ACTIVE";
		if (shouldSetFsdDeliveryDate) {
			return {
				...result,
				fsdDeliveryDate: this._data.deliveryDate.slice(0, 10),
			};
		}
		return result;
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SET_DELIVERY_DATE" }),
			id,
		};
	}
}
// no-op payload
export class OrderHeaderChangedPayload implements CartEventPayload {
	readonly type = "OrderHeaderChangedPayload";
	_data!: Record<string, unknown>;

	applyTo(cart: CustomerCart): CustomerCart {
		return { ...cart };
	}

	get data() {
		return this._data;
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_CHANGE_ORDER_HEADER" }),
			id,
		};
	}
}
export class CheckedOutPayload implements CartEventPayload {
	readonly type = "CheckedOutPayload";
	_data: {
		checkoutId: string | null | undefined;
		removedBundleIds: Array<string> | null | undefined;
	};

	constructor(data: $PropertyType<CheckedOutPayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo(cart: CustomerCart): CustomerCart | null {
		return {
			...cart,
			orderData: { ...cart.orderData, checkoutId: this._data.checkoutId },
			items: omit(cart.items, this._data.removedBundleIds || []) as CustomerCartItems,
		};
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_CHECKOUT_NOT_CREATED" }),
			id,
		};
	}
}
export class CartSubmittedPayload implements CartEventPayload {
	readonly type = "CartSubmittedPayload";
	_data: {
		submittedItems: Array<string>;
		checkoutId: string;
		submitId: string;
	};

	constructor(data: $PropertyType<CartSubmittedPayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo(cart: CustomerCart): CustomerCart | null {
		const { submittedItems } = this._data;
		const items = { ...cart.items };
		submittedItems.forEach((bundleId) => delete items[bundleId]);

		if (Object.keys(items).length > 0) {
			// delete checkout ID if it is a match
			const checkoutId = this._data.checkoutId === cart.orderData.checkoutId ? null : this._data.checkoutId;
			return { ...cart, items, orderData: { ...cart.orderData, checkoutId } };
		}

		return null;
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SUBMIT_CONFIRMATION" }),
			id,
		};
	}
}
export class CartAwaitingHipayPaymentPayload implements CartEventPayload {
	readonly type = "CartAwaitingHipayPaymentPayload";
	_data: {
		checkoutId: string;
	};

	constructor(data: $PropertyType<CartAwaitingHipayPaymentPayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo() {
		return null;
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_HIPAY_PAYMENT_SUBMISSION" }),
			id,
		};
	}
}
export class CartRestockedPayload implements CartEventPayload {
	readonly type = "CartRestockedPayload";
	_data: {
		addedItemData: Record<
			string,
			{
				bundleId: string;
				quantity: number;
				comment: string | null | undefined;
				addedTimestamp: number;
				addedBy: string;
			}
		>;
	};

	constructor(data: $PropertyType<CartRestockedPayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo(cart: CustomerCart, status: EventStatus): CustomerCart {
		const { addedItemData } = this._data;
		return Object.values(addedItemData).reduce((agg, itemData) => {
			if (!(itemData instanceof Object)) return agg;
			const { bundleId, quantity, comment, addedTimestamp, addedBy } = itemData;
			return applyAddItem(agg, bundleId, quantity, status, comment, addedTimestamp, addedBy);
		}, cart);
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_CART_RESTOCK" }),
			id,
		};
	}
}
export class SavedCartLoadedPayload implements CartEventPayload {
	readonly type = "SavedCartLoadedPayload";
	_data: {
		addedItemData: Record<
			string,
			{
				bundleId: string;
				quantity: number;
				comment: string | null | undefined;
				addedTimestamp: number;
				addedBy: string;
			}
		>;
	};

	constructor(data: $PropertyType<SavedCartLoadedPayload, "_data">) {
		this._data = data;
	}

	get data() {
		return this._data;
	}

	applyTo(cart: CustomerCart, status: EventStatus): CustomerCart {
		const { addedItemData } = this._data;
		return Object.values(addedItemData).reduce((agg, itemData) => {
			if (!(itemData instanceof Object)) return agg;
			const { bundleId, quantity, comment, addedTimestamp, addedBy } = itemData;
			return applyAddItem(agg, bundleId, quantity, status, comment, addedTimestamp, addedBy);
		}, cart);
	}

	getErrorMessage(id: string): IntlNotification {
		return {
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_LOAD_SAVED_CART" }),
			id,
		};
	}
}
export function isEventPending(event: CustomerCartEvent): boolean {
	return event.status.type === "Pending";
}
export function parseServerEvent(json: Record<string, any>): CustomerCartEvent {
	const { eventId, cartId, customerId, seqNum, status, prices, timestamp, movPerSupplierInfo } = json;
	return new CustomerCartEvent(
		{
			eventId,
			cartId,
			customerId,
			seqNum,
			status,
			prices,
			timestamp,
			movPerSupplierInfo,
		},
		parsePayload(json.payload),
	);
}
export function parseLocalEvent(json: Record<string, any>): CustomerCartEvent {
	const { eventId, cartId, customerId, seqNum, status, prices, timestamp, movPerSupplierInfo } = json._data;
	return new CustomerCartEvent(
		{
			eventId,
			cartId,
			customerId,
			seqNum,
			status,
			prices,
			timestamp,
			movPerSupplierInfo,
		},
		parsePayload({ ...json._payload._data, type: json._payload.type }),
	);
}

function parsePayload(json: Record<string, any>): CartEventPayload {
	switch (json.type) {
		case "CartCreatedPayload": {
			const { country, cardholderNumber, storeId, fsdAddressId } = json;
			return new CartCreatedPayload({
				country,
				cardholderNumber,
				storeId,
				fsdAddressId,
			});
		}

		case "CartDeletedPayload": {
			return new CartDeletedPayload();
		}

		case "CartSavedPayload": {
			const { cartName, clearCart, savedCartId } = json;
			return new CartSavedPayload({
				cartName,
				clearCart,
				savedCartId,
			});
		}

		case "ItemAddedPayload": {
			const { bundleId, quantity, comment, addedTimestamp, addedBy } = json;
			return new ItemAddedPayload({
				bundleId,
				quantity,
				comment,
				addedTimestamp,
				addedBy,
			});
		}

		case "ItemBatchAddedPayload": {
			const { addedItemData } = json;
			return new ItemBatchAddedPayload({
				addedItemData,
			});
		}

		case "ItemQuantitySetPayload": {
			const { bundleId, quantity, addedTimestamp, addedBy } = json;
			return new ItemQuantitySetPayload({
				bundleId,
				quantity,
				addedTimestamp,
				addedBy,
			});
		}

		case "ItemServiceOptionsSetPayload": {
			const { bundleId, serviceOptionIds } = json;
			return new ItemServiceOptionsSetPayload({
				bundleId,
				serviceOptionIds,
			});
		}

		case "ItemCommentSetPayload": {
			const { bundleId, comment } = json;
			return new ItemCommentSetPayload({
				bundleId,
				comment,
			});
		}

		case "ItemRemovedPayload": {
			const { bundleId } = json;
			return new ItemRemovedPayload({
				bundleId,
			});
		}

		case "ItemManualPriceSetPayload": {
			const { bundleId, newPrice, reset, isBbd, approved } = json;
			return new SetManualPricePayload({
				bundleId,
				newPrice,
				reset,
				isBbd,
				approved,
			});
		}

		case "DeliveryDateSetPayload": {
			const { deliveryDate } = json;
			return new DeliveryDateSetPayload({
				deliveryDate,
			});
		}

		case "OrderHeaderChangedPayload": {
			return new OrderHeaderChangedPayload();
		}

		case "CheckedOutPayload": {
			const { checkoutId, removedBundleIds } = json;
			return new CheckedOutPayload({
				checkoutId,
				removedBundleIds,
			});
		}

		case "CartSubmittedPayload": {
			const { submittedItems, checkoutId, submitId } = json;
			return new CartSubmittedPayload({
				submittedItems,
				checkoutId,
				submitId,
			});
		}

		case "CartAwaitingHipayPaymentPayload": {
			const { checkoutId } = json;
			return new CartAwaitingHipayPaymentPayload({
				checkoutId,
			});
		}

		case "CartRestockedPayload": {
			const { addedItemData } = json;
			return new CartRestockedPayload({
				addedItemData,
			});
		}

		case "SavedCartLoadedPayload": {
			const { addedItemData } = json;
			return new SavedCartLoadedPayload({
				addedItemData,
			});
		}

		default:
			throw new ServiceError({
				code: "OC-804",
				message: `Could not parse event payload: ${JSON.stringify(json)}`,
				type: "ui-error",
			});
	}
}
